import { api } from '../../lib/api';
import { brand } from '../../config/brand';

/**
 * Badges, as the server describes them.
 *
 * The catalogue lives in `App\Domain\Achievements\Badges` and nothing is
 * duplicated here - not the labels, not the targets, not which badges exist.
 * Same argument as the weighting legend: copy that explains a rule must not be
 * able to describe a rule the server does not apply, and the only way to
 * guarantee that is to render what it sent.
 */
export interface Badge {
  slug: string;
  label: string;
  blurb: string;
  /** What you have to do, in the server's words, with the server's numbers. */
  how: string;
  target: number;
  icon: { small: string; large: string };
  earned: boolean;
  /** Null on a badge nobody has earned yet. */
  earned_at: string | null;
  /**
   * How far along, capped at the target.
   *
   * Only meaningful when the response is your own - see `mine`. A stranger's
   * response carries earned badges only, and progress equal to the target.
   */
  progress: number;
}

export interface BadgeList {
  items: Badge[];
  /** How many badges exist, so "5 of 11" is the server's count and not a guess. */
  total: number;
  /**
   * Whether this is your own list, and therefore whether the locked badges and
   * their progress are in it.
   *
   * Progress measures somebody's listening habits, which is not a thing to hand
   * to anybody who loads their profile.
   */
  mine: boolean;
}

/**
 * Badge art is served by the API's own origin, not by Vite.
 *
 * `location /badges` sits beside `/embed` and `/overlay` in nginx for the same
 * reason those do: `location /` roots at the built SPA, so anything under
 * `backend/public` is unreachable without one. In development the SPA is on a
 * different port entirely, so the path has to be absolute either way.
 */
export function badgeIcon(path: string): string {
  return `${brand.url}${path}`;
}

export const achievements = {
  forUser: (username: string) => api.get<BadgeList>(`/users/${username}/achievements`),
};
